const _ = require('lodash')
// Shared imports
const cloudinary = require('shared/cloudinary')
const cssUtils = require('shared/css-utils')
// Client V1 imports
const airbrake = require('client/lib/airbrake')
const businessEvents = require('client/lib/business-events')
const { send: ajax, FriendlyRequestError } = require('client/lib/request')
const uploader = require('client/lib/uploader')
const { alert, confirm } = require('client/components/confirm')
const Modal = require('client/components/modal')
// Client V2 imports
const headerImageModalActions = require('client-v2/collection-profile/containers/header-image-modal/actions')

require('client/lib/jquery/boolean-attributes')
require('client/lib/jquery/register-plugin')

const MAX_TITLE_LENGTH = 50
const MAX_DESCRIPTION_LENGTH = 150

async function saveMixAjax(mixId, data) {
  const response = await ajax({
    url: `/api/v0.1/mix/${mixId}`,
    method: 'put',
    data
  })
  // Send tracking call
  await businessEvents.trackSegment('update-mix', {
    'mix_id': mixId
  })
  return response
}

async function deleteMixAjax(mixId) {
  const response = await ajax({
    url: `/api/v0.1/mix/${mixId}`,
    method: 'delete'
  })
  // Send tracking call
  await businessEvents.trackSegment('delete-mix', {
    'mix_id': mixId
  })
  return response
}

function uploadMixHeaderImageAjax(mixId, file) {
  const url = `/api/v0.1/mix/${mixId}/upload-header-image`
  const name = 'mix_header_image'
  return uploader(url, name, file)
  .then(headerImageUrl => {
    return cloudinary.convertMixModelHeaderImageUrl({
      'header_image_url': headerImageUrl
    })
  })
}

function deleteMixHeaderImageAjax(mixId) {
  return ajax({
    url: `/api/v0.1/mix/${mixId}/upload-header-image`,
    method: 'delete'
  })
}

function fetchImages(mixId, page = 1) {
  return ajax({
    url: `/api/v0.1/mix/${mixId}/posts/${page}`,
    method: 'get'
  })
  .then(({ items, meta }) => {
    const images = items.map(item => item.thumbnailImage)
    return {
      images: _.compact(images),
      nextPage: items.length && meta.nextUrl ? page + 1 : false
    }
  })
}

const EVENT_HANDLER_METHODS = [
  'chooseCoverImage',
  'deleteMix',
  'onEnterKeyDown',
  'saveMix',
  'showHeaderImageModal',
  'updateCountdown'
]

module.exports = class MixEditModal extends Modal {
  constructor(options) {
    // Handle options
    if (!_.get(options, 'mix.id')) {
      throw Error('Required option `mix` is not an Object with `id` property')
    }
    super(_.defaults(options, {
      modalTemplate: require('*views/modals/mix-edit/details'),
      modalClassName: 'mix-edit-details-modal',
      modalTitle: 'Edit Collection'
    }))
    this.mix = Object.assign({}, options.mix)
    // Class method usage convenience
    _.bindAll(this, EVENT_HANDLER_METHODS)
  }

  didRender() {
    // Inherit dismissal listeners
    super.didRender()
    // Cache jQuery handles
    this.$headerImage = this.$el.find('.mix-edit-details-modal__header-image')
    this.$headerUpload = this.$el.find('.mix-edit-details-modal__header-upload')
    this.$headerLabel = this.$el.find('.mix-edit-details-modal__header-label')
    this.$coverImage = this.$el.find('.mix-edit-details-modal__cover-image')
    this.$title = this.$el.find('[name="title"]')
    this.$description = this.$el.find('[name=description]')
    this.$countdown = this.$el.find('.mix-edit-details-modal__description-countdown')
    this.$visibility = this.$el.find('[name=visibility]')
    // Setup initial state
    this.$title.attr('maxlength', MAX_TITLE_LENGTH)
    this.$description.attr('maxlength', MAX_DESCRIPTION_LENGTH)
    this.updateCountdown()
    // Register event listeners
    this.$el.on('click', '.mix-edit-details-modal__delete-button', this.deleteMix)
    this.$el.on('click', '.mix-edit-details-modal__save-button', this.saveMix)
    this.$el.on('submit', '.mix-edit-details-modal__details-form', this.saveMix)
    this.$title.on('keydown', this.onEnterKeyDown)
    this.$description.on('keydown', this.onEnterKeyDown)
    this.$description.on('input', this.updateCountdown)
    this.$coverImage.on('click', this.chooseCoverImage)
    this.$headerImage.on('click', this.showHeaderImageModal)
    this.$headerUpload.on('change', this.showHeaderImageModal)
  }

  dismiss() {
    this.resolve(this.mix)
  }

  onEnterKeyDown(event) {
    if (event.keyCode === 13) {
      this.saveMix(event)
    }
  }

  updateCountdown() {
    const description = this.$description.val()
    const charsTotal = description.length
    if (charsTotal < MAX_DESCRIPTION_LENGTH) {
      this.$countdown.text(MAX_DESCRIPTION_LENGTH - charsTotal)
    } else {
      this.$countdown.text('0')
      this.$description.val(description.substr(0, MAX_DESCRIPTION_LENGTH))
    }
  }

  showHeaderImageModal(event) {
    event.preventDefault()
    const droppedFile = this.$headerUpload[0].files[0]
    this.$el.hide()
    // Callback to run once the header-image-modal has been dismissed
    const onHeaderImageModalDismissed = () => {
      this.$el.show()
      if (droppedFile) {
        // Reset the $headerUpload <input type="file" /> value
        // https://css-tricks.com/snippets/jquery/clear-a-file-input/
        const $resetHeaderUpload =this.$headerUpload.val('').clone(true)
        this.$headerUpload.replaceWith($resetHeaderUpload)
        this.$headerUpload = $resetHeaderUpload
      }
    }
    app.store.dispatch(headerImageModalActions.show({
      droppedFile,
      savedImageUrl: _.get(this.mix, 'header_image_urls.small'),
      onDismiss: () => {
        onHeaderImageModalDismissed()
      },
      onDelete: () => {
        onHeaderImageModalDismissed()
        this.deleteHeaderImage()
      },
      onSubmit: file => {
        onHeaderImageModalDismissed()
        this.uploadHeaderImage(file)
      }
    }))
  }

  processHeaderImageChange(asyncFunction) {
    // Set "processing" state
    this.$headerUpload.prop('disabled', true)
    this.$headerImage.addClass('mix-edit-details-modal__header-image--processing')
    this.$headerLabel.text('Processing...')
    asyncFunction()
    .finally(() => {
      // Remove "processing" state
      this.$headerUpload.prop('disabled', false)
      this.$headerImage.removeClass('mix-edit-details-modal__header-image--processing')
      this.$headerLabel.text('Edit Collection Banner Image')
    })
  }

  uploadHeaderImage(file) {
    this.processHeaderImageChange(() => {
      const mixId = this.mix.id
      // Perform the upload
      return uploadMixHeaderImageAjax(mixId, file)
      .then(headerImageUrlFields => {
        // Update the mix model
        Object.assign(this.mix, headerImageUrlFields)
        // Update the DOM
        const imageUrl = _.get(this.mix, 'header_image_urls.small')
        this.$headerImage.attr('style', cssUtils.backgroundImage(imageUrl))
        this.$headerImage.removeClass('mix-edit-details-modal__header-image--default')
      })
      .catch(err => {
        airbrake.notify({
          error: err,
          params: { mixId }
        })
        const subheading = err instanceof FriendlyRequestError
        ? err.message
        : 'We were not able to use that image.'
        return alert({
          heading: 'Sorry.',
          subheading
        })
      })
    })
  }

  deleteHeaderImage() {
    this.processHeaderImageChange(() => {
      const mixId = this.mix.id
      return deleteMixHeaderImageAjax(mixId)
      .then(() => {
        // Update the mix model
        Object.assign(this.mix, {
          'header_image_url': '',
          'header_image_urls': null
        })
        // Update the DOM
        this.$headerImage.attr('style', '')
        this.$headerImage.addClass('mix-edit-details-modal__header-image--default')
      })
      .catch(err => {
        airbrake.notify({
          error: err,
          params: { mixId }
        })
        const subheading = err instanceof FriendlyRequestError
        ? err.message
        : 'We were not able to process your request at this time.'
        return alert({
          heading: 'Sorry.',
          subheading
        })
      })
    })
  }

  updateCoverImage(imageUrl) {
    // Update the mix model
    const oldCoverImageUrl = this.mix['cover_image_url']
    if (oldCoverImageUrl && this.mix.images[0] === oldCoverImageUrl) {
      this.mix.images = this.mix.images.slice(1)
    }
    this.mix['cover_image_url'] = imageUrl
    if (imageUrl) {
      this.mix.images = [imageUrl, ...this.mix.images]
    } else {
      imageUrl = _.first(this.mix.images)
    }
    // Update the DOM
    this.$coverImage.attr('style', cssUtils.backgroundImage(imageUrl))
  }

  chooseCoverImage(event) {
    event.preventDefault()
    const mixId = this.mix.id
    const defaultValue = this.mix['cover_image_url']
    this.$el.hide()
    return MixEditCoverImageModal.promise({ defaultValue, mixId })
    .then(imageUrl => {
      this.$el.show()
      if (imageUrl === defaultValue) {
        return // No need to save; nothing has changed
      }
      return saveMixAjax(mixId, {
        'cover_image_url': imageUrl || ''
      })
      .then(mix => {
        this.updateCoverImage(mix['cover_image_url'])
      })
      .catch(err => {
        airbrake.notify({
          error: err,
          params: { mixId, imageUrl }
        })
        const subheading = err instanceof FriendlyRequestError
        ? err.message
        : 'We were not able to use that image.'
        return alert({
          heading: 'Sorry.',
          subheading
        })
      })
    })
  }

  saveMix(event) {
    event.preventDefault()
    const mixId = this.mix.id
    const isPrivate = this.$visibility.boolAttr('checked')
    return saveMixAjax(mixId, {
      name: this.$title.val(),
      description: this.$description.val(),
      visibility: isPrivate ? 'private' : 'public'
    })
    .then(response => {
      this.resolve(Object.assign({}, this.mix, {
        name: response.name,
        description: response.description || '',
        visibility: response.visibility
      }))
    })
    .catch(err => {
      airbrake.notify({
        error: err,
        params: { mixId }
      })
      const subheading = err instanceof FriendlyRequestError
      ? err.message
      : 'We were unable to update this collection.'
      return alert({
        heading: 'Sorry.',
        subheading
      })
    })
  }

  deleteMix(event) {
    event.preventDefault()
    const mixId = this.mix.id
    confirm({
      heading: 'Are you sure you want to delete this collection?',
      subheading: 'Your posts here will be moved into your "General" collection.'
    })
    .then(confirmed => {
      return confirmed && deleteMixAjax(mixId)
      .then(response => {
        // Fake a response object that indicates the mix is gone
        this.resolve({
          ok: response === 'OK',
          id: mixId,
          deleted: true
        })
      })
      .catch(err => {
        airbrake.notify({
          error: err,
          params: { mixId }
        })
        const subheading = err instanceof FriendlyRequestError
        ? err.message
        : 'We were unable to delete this collection.'
        return alert({
          heading: 'Sorry.',
          subheading
        })
      })
    })
    .catch(() => {
      return null // do nothing
    })
  }
}

class MixEditCoverImageModal extends Modal {
  constructor(options) {
    super(_.defaults(options, {
      modalClassName: 'mix-edit-cover-image-modal',
      modalTemplate: require('*views/modals/mix-edit/cover-image'),
      itemsTemplate: require('*views/modals/mix-edit/cover-image-items')
    }))
    // Class method usage convenience
    _.bindAll(this, ['handleSelection', 'handleScroll'])
    this.handleScroll = _.throttle(this.handleScroll, 100)
    // Pagination state
    this.images = options.defaultValue ? [options.defaultValue] : []
    this.isFetchingNextPage = false
    this.nextPage = 1
  }

  willRender(templateOptions) {
    templateOptions.images = this.images
  }

  didRender() {
    // Inherit dismissal listeners
    super.didRender()
    // Cache jQuery handles
    this.$imageList = this.$el.find('.mix-edit-cover-image-modal__items-container')
    this.$scrollContainer = this.$el.find('.mix-edit-cover-image-modal__content')
    // Dismiss when clicking the back arrow
    this.$el.on('click', '.mix-edit-cover-image-modal__header', this.dismiss)
    // Override default checkbox behavior
    this.$imageList.on('click', 'input', this.handleSelection)
    // Setup paginated scrolling
    this.$scrollContainer.on('scroll', this.handleScroll)
    _.delay(this.handleScroll, 0) // Wait for DOM to layout
  }

  get selected() {
    return this.$imageList.find('input:checked').val()
  }

  dismiss() {
    this.resolve(this.selected)
  }

  handleSelection(event) {
    // Ensure only a single image can be selected
    this.$imageList.find('input:checked')
    .not(event.currentTarget)
    .boolAttr('checked', false)
  }

  handleScroll() {
    const { scrollTop, scrollHeight, clientHeight } = this.$scrollContainer.get(0)
    const scrollBottom = scrollTop + clientHeight
    const paginateThreshold = scrollHeight - 120
    if (scrollBottom < paginateThreshold) {
      return
    }
    if (!this.nextPage) {
      // Disable pagination; there are no more pages to load.
      this.$scrollContainer.off('scroll')
      return
    }
    this.fetchNextPage()
  }

  fetchNextPage() {
    if (this.isFetchingNextPage || !this.nextPage) {
      return
    }
    this.isFetchingNextPage = true
    fetchImages(this.options.mixId, this.nextPage)
    .then(result => {
      // Add new images to the list
      if (result.images.length) {
        const newImages = _.without(result.images, ...this.images)
        const $imageItems = this.$el.find('.mix-edit-cover-image-modal__item')
        this.$imageList.append(this.options.itemsTemplate({
          images: newImages,
          imagesIndexOffset: $imageItems.length,
          backgroundImage: cssUtils.backgroundImage
        }))
        this.images.push(...newImages)
      }
      // Update modal pagination state
      this.nextPage = result.nextPage
      this.isFetchingNextPage = false
      _.delay(this.handleScroll, 0) // Wait for DOM to layout
    })
    .catch(err => { airbrake.notify(err) })
  }
}
